"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Search, Loader2 } from "lucide-react";
import { DependentControl } from "../types";

interface DependentControlSelectorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (controls: DependentControl[]) => void;
  existingControls?: DependentControl[];
}

interface RcmControl {
  ControlId?: string;
  ControlName?: string;
  control_name?: string;
  TypeOfControl?: string;
  Nature?: string;
  [key: string]: any;
}

const DependentControlSelectorDialogComponent: React.FC<DependentControlSelectorDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  existingControls = [],
}) => {
  const [rcmControls, setRcmControls] = useState<Record<string, RcmControl>>({});
  const [selectedControls, setSelectedControls] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const existingControlsRef = useRef<DependentControl[]>([]);
  const prevOpenRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const onSelectRef = useRef(onSelect);

  // Stabilize refs to prevent callback recreation issues
  onCloseRef.current = onClose;
  onSelectRef.current = onSelect;

  // Memoize existingControls IDs to detect actual changes
  const existingControlsIds = useMemo(() => {
    return existingControls.map(c => c.controlId).join(',');
  }, [existingControls]);

  // Update ref only when the array actually changes
  useEffect(() => {
    const currentIds = existingControlsRef.current.map(c => c.controlId).join(',');
    if (currentIds !== existingControlsIds) {
      existingControlsRef.current = existingControls;
    }
  }, [existingControls, existingControlsIds]);

  const loadRcmControls = useCallback(async () => {
    setLoading(true);
    try {
      if (window.sharePointAPI && typeof window.sharePointAPI.downloadRcmJson === 'function') {
        const result = await window.sharePointAPI.downloadRcmJson();
        
        if (result.success && result.data) {
          const librariesRcm = result.data.libraries_RCM || result.data.libraries_rcm || {};
          setRcmControls(librariesRcm);
        } else {
          setRcmControls({});
        }
      } else {
        setRcmControls({});
      }
    } catch (error) {
      console.error('Error loading RCM controls:', error);
      setRcmControls({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize selected controls only when the dialog opens. Defer to next tick to avoid
  // "Maximum update depth" with Radix Dialog's setRef running in the same commit.
  useEffect(() => {
    if (isOpen) {
      if (!prevOpenRef.current) {
        // Use requestAnimationFrame for better timing with Radix UI ref management
        let timeoutId: NodeJS.Timeout | undefined;
        const rafId = requestAnimationFrame(() => {
          timeoutId = setTimeout(() => {
            const controls = existingControlsRef.current;
            if (controls && controls.length > 0) {
              setSelectedControls(new Set(controls.map(control => control.controlId)));
            } else {
              setSelectedControls(new Set());
            }
            prevOpenRef.current = true;
          }, 0);
        });
        return () => {
          cancelAnimationFrame(rafId);
          if (timeoutId) clearTimeout(timeoutId);
        };
      }
    } else {
      prevOpenRef.current = false;
      setSearchTerm("");
    }
  }, [isOpen]);

  // Load controls from Libraries_RCM.json when dialog opens.
  // Defer to next tick to avoid update depth issues with Radix Dialog ref management.
  useEffect(() => {
    if (!isOpen) return;
    let timeoutId: NodeJS.Timeout;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        loadRcmControls();
      }, 0);
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen, loadRcmControls]);

  const handleToggleControl = (controlId: string) => {
    setSelectedControls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(controlId)) {
        newSet.delete(controlId);
      } else {
        newSet.add(controlId);
      }
      return newSet;
    });
  };

  const handleConfirm = useCallback(() => {
    const selected = Array.from(selectedControls)
      .map(controlId => {
        const controlData = rcmControls[controlId];
        if (!controlData) return null;
        
        // Helper to extract string from textResponse or string
        const extractString = (value: any): string => {
          if (typeof value === 'string') return value;
          if (value && typeof value === 'object' && 'plainText' in value) {
            return value.plainText || value.html || '';
          }
          return '';
        };
        
        return {
          id: `dep-${controlId}-${Date.now()}`,
          controlId: controlId,
          controlName: extractString(controlData.ControlName || controlData.control_name) || "",
          controlDescription: extractString(controlData.TypeOfControl) || "",
          designImplementation: extractString(controlData.Nature) || "",
          operatingEffectiveness: "", // Keep for compatibility but not displayed
        };
      })
      .filter((control): control is DependentControl => control !== null);
    
    onSelectRef.current(selected);
    onCloseRef.current();
  }, [selectedControls, rcmControls]);

  const handleCancel = useCallback(() => {
    setSearchTerm("");
    onCloseRef.current();
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      handleCancel();
    }
  }, [handleCancel]);

  // Helper function to extract string from textResponse object or string
  const extractString = (value: any): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'plainText' in value) {
      return value.plainText || value.html || '';
    }
    return '';
  };

  // Filter controls based on search term
  const filteredControls = Object.entries(rcmControls).filter(([controlId, controlData]) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    const controlIdLower = controlId.toLowerCase();
    const controlName = extractString(controlData.ControlName || controlData.control_name).toLowerCase();
    const typeOfControl = extractString(controlData.TypeOfControl).toLowerCase();
    const nature = extractString(controlData.Nature).toLowerCase();
    return controlIdLower.includes(search) || controlName.includes(search) || typeOfControl.includes(search) || nature.includes(search);
  });

  // Only mount Dialog when open to avoid Radix setRef update loops (maximum update depth)
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-black/90 border-white/10 text-white max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white">Select Dependent Controls</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              type="text"
              placeholder="Search controls by ID, name, type, or nature..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-black/40 border-white/20 text-white placeholder:text-white/40"
            />
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-white/60" />
              <span className="ml-2 text-white/60">Loading controls...</span>
            </div>
          ) : filteredControls.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-white/60">
              {searchTerm ? "No controls found matching your search." : "No controls available."}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto border border-white/10 rounded-lg">
              <div className="p-2 space-y-2">
                {filteredControls.map(([controlId, controlData]) => {
                  const isSelected = selectedControls.has(controlId);
                  const controlName = extractString(controlData.ControlName || controlData.control_name) || "Unnamed Control";
                  const typeOfControl = extractString(controlData.TypeOfControl) || "N/A";
                  const nature = extractString(controlData.Nature) || "N/A";
                  
                  return (
                    <label
                      key={controlId}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 cursor-pointer border border-transparent hover:border-white/10 transition-colors"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleControl(controlId)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-white">
                          {controlId}
                        </div>
                        <div className="text-sm text-white/80 mt-1">
                          {controlName}
                        </div>
                        <div className="text-xs text-white/60 mt-1">
                          <span className="font-medium">Type:</span> {typeOfControl} | <span className="font-medium">Nature:</span> {nature}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selection Count */}
          {selectedControls.size > 0 && (
            <div className="text-sm text-white/60">
              {selectedControls.size} control{selectedControls.size !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-white/10 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-primary text-white hover:bg-primary/80"
            disabled={selectedControls.size === 0}
          >
            Confirm ({selectedControls.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const DependentControlSelectorDialog = React.memo(DependentControlSelectorDialogComponent);

